import { outbox } from '../lib/outbox'
import { create } from 'zustand'

import { applyOnboardingState, clearPendingAnswers, keepAnswersAfter, readPendingAnswers } from '../lib/onboarding'
import { onAuthExpired, tokenStore } from '../services/api'
import * as authApi from '../services/auth'
import { completeOnboarding } from '../services/onboarding'

/**
 * How soon after the last read a focus / visibility refresh may ask the
 * server again. A scored attempt bypasses it (`force`).
 */
export const PROFILE_REFRESH_MIN_MS = 15_000

let profileReadAt = 0
let profileInFlight = null
/** Bumped by every write to `user`, so a read that started before it is dropped. */
let userVersion = 0

/**
 * Session state.
 *
 * `status` is a three-state value rather than a boolean: on first paint the
 * app does not yet know whether the stored token is still valid, and routing
 * on `!user` during that moment would bounce a signed-in user to the login
 * page on every refresh.
 */
export const useAuthStore = create((set, get) => ({
  user: null,
  status: 'loading', // 'loading' | 'authenticated' | 'anonymous'
  error: null,

  /** Restore the session from a stored token, once, at startup. */
  async bootstrap() {
    if (!tokenStore.access) {
      set({ status: 'anonymous', user: null })
      return
    }
    try {
      const user = await authApi.fetchProfile()
      profileReadAt = Date.now()
      userVersion += 1
      set({ user, status: 'authenticated', error: null })
    } catch {
      tokenStore.clear()
      set({ user: null, status: 'anonymous' })
      return
    }
    await get().syncOnboarding()
  },

  /**
   * Read `/me/profile` again so the XP and the server's `features` (the
   * switches the owner flips) are current without a reload — on coming back
   * to the tab and after each scored attempt (the Android app re-reads on
   * every visit to Home / Profile). Cheap by construction: one request at a
   * time, and at most one per `PROFILE_REFRESH_MIN_MS` unless `force`. Never
   * throws and never signs anyone out: a failed read keeps what is on screen
   * (a 401 is the refresh interceptor's business, `onAuthExpired`).
   */
  async refreshProfile({ force = false } = {}) {
    if (get().status !== 'authenticated') return
    if (profileInFlight) return profileInFlight
    if (!force && Date.now() - profileReadAt < PROFILE_REFRESH_MIN_MS) return
    const startedFor = get().user?.id ?? null
    const startedAt = userVersion
    profileInFlight = (async () => {
      try {
        const fresh = await authApi.fetchProfile()
        profileReadAt = Date.now()
        const current = get()
        // Signed out, another account, or the form saved a newer profile meanwhile: this read is stale.
        if (current.status !== 'authenticated' || (current.user?.id ?? null) !== startedFor || userVersion !== startedAt) return
        userVersion += 1
        set({ user: { ...current.user, ...fresh } })
      } catch {
        // Keep the profile on screen; the next focus or attempt asks again.
      } finally {
        profileInFlight = null
      }
    })()
    return profileInFlight
  },

  /**
   * The two onboarding answers kept on the device (V3-w) go up with the
   * first session that has them — after sign-in, and again on every app
   * open while a send has failed. A refusal the server meant
   * (`lib/onboarding.js` `keepAnswersAfter`) drops them instead; the
   * questions then come back once after sign-in. Never throws: the
   * session does not depend on it.
   */
  async syncOnboarding() {
    const pending = readPendingAnswers()
    if (!pending || get().status !== 'authenticated') return
    try {
      const state = await completeOnboarding(pending)
      clearPendingAnswers()
      const user = get().user
      if (user) {
        userVersion += 1
        set({ user: applyOnboardingState(user, state) })
      }
    } catch (error) {
      if (!keepAnswersAfter(error)) clearPendingAnswers()
    }
  },

  async requestOtp(phone) {
    set({ error: null })
    return authApi.requestOtp(phone)
  },

  async verifyOtp(phone, code) {
    await authApi.verifyOtp(phone, code)
    // The token response's `user` is the auth-handshake shape and has no XP;
    // /me/profile is the one shape every screen after login actually reads.
    const user = await authApi.fetchProfile()
    profileReadAt = Date.now()
    userVersion += 1
    set({ user, status: 'authenticated', error: null })
    // Not awaited: sign-in is done; the answers can land a moment later.
    void get().syncOnboarding()
    return user
  },

  signOut() {
    authApi.logout()
    // The takes kept on this device were this learner's; the next one must not find them (PM audit 2026-10-14).
    void outbox.clear()
    profileReadAt = 0
    userVersion += 1
    set({ user: null, status: 'anonymous' })
  },

  /** The profile as the server now has it — after `me_updateProfile` returns it. */
  setUser(user) {
    userVersion += 1
    set({ user })
  },

  setError(error) {
    set({ error })
  },
}))

// A refresh that fails anywhere in the app ends the session here, so every
// screen reacts to expiry the same way.
onAuthExpired(() => {
  useAuthStore.setState({ user: null, status: 'anonymous' })
})
